import React, { useState } from "react";
import { api } from "@/lib/api";
import { fmtEUR, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Landmark, CheckCircle2, AlertTriangle, Loader2, Ban } from "lucide-react";
import { toast } from "sonner";

export function BankImport({ onApplied }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState(null);
  const [done, setDone] = useState(null);

  const reset = () => { setPreview(null); setDone(null); };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setOpen(true); reset(); setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/verkoopfacturen/bank-import/preview", fd);
      setPreview(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kon bestand niet inlezen");
      setOpen(false);
    } finally { setLoading(false); }
  };

  const apply = async () => {
    const nMatch = preview?.matches?.length || 0;
    const nCredit = preview?.credit_matches?.length || 0;
    if (!nMatch && !nCredit) return;
    setApplying(true);
    try {
      const markeringen = (preview.matches || []).map((m) => ({ factuur_id: m.factuur_id, betaaldatum: m.betaaldatum, transactie_bedrag: m.transactie_bedrag }));
      const credit_markeringen = (preview.credit_matches || []).map((m) => ({ factuur_id: m.factuur_id, verwerkt_op: m.verwerkt_op }));
      const { data } = await api.post("/verkoopfacturen/bank-import/apply", { markeringen, credit_markeringen });
      setDone(data);
      const deels = data.deels_betaald_gemarkeerd || 0;
      const credit = data.credit_verwerkt_gemarkeerd || 0;
      toast.success(`${data.betaald_gemarkeerd} betaald${deels ? `, ${deels} deels betaald` : ""}${credit ? `, ${credit} credit verwerkt` : ""}`);
      onApplied?.();
    } catch {
      toast.error("Markeren mislukt");
    } finally { setApplying(false); }
  };

  return (
    <>
      <input id="bank-file-input" type="file" accept=".sta,.STA,.txt,.940,.mt940" className="hidden"
        onChange={onFile} data-testid="bank-file-input" />
      <Button variant="outline" onClick={() => document.getElementById("bank-file-input").click()}
        data-testid="bank-import-btn">
        <Landmark className="h-4 w-4 mr-1" /> Bankbestand (MT940)
      </Button>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
        <DialogContent className="max-w-2xl" data-testid="bank-import-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Landmark className="h-5 w-5 text-primary" /> Bankbestand importeren (MT940)</DialogTitle>
          </DialogHeader>

          {loading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-10 justify-center">
              <Loader2 className="h-4 w-4 animate-spin" /> Bestand inlezen…
            </div>
          )}

          {done && (
            <div className="space-y-2 text-sm py-8 text-center" data-testid="bank-import-done">
              <CheckCircle2 className="h-9 w-9 text-emerald-500 mx-auto" />
              <div className="font-medium text-base">{done.betaald_gemarkeerd} factuur(en) volledig betaald</div>
              {done.deels_betaald_gemarkeerd > 0 && (
                <div className="text-amber-600">{done.deels_betaald_gemarkeerd} factuur(en) deels betaald</div>
              )}
              {done.credit_verwerkt_gemarkeerd > 0 && (
                <div className="text-rose-600">{done.credit_verwerkt_gemarkeerd} creditfactuur(en) op verwerkt gezet</div>
              )}
            </div>
          )}

          {!loading && !done && preview && (
            <div className="space-y-3 text-sm">
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline">{preview.totaal_transacties} transacties</Badge>
                <Badge variant="outline" className="text-sky-600 border-sky-300">{preview.credit_transacties} bijschrijvingen</Badge>
                <Badge variant="outline" className="text-emerald-600 border-emerald-300">{preview.matches.length} te markeren</Badge>
                {preview.credit_matches?.length > 0 && <Badge variant="outline" className="text-rose-600 border-rose-300">{preview.credit_matches.length} credit verwerken</Badge>}
                {preview.reeds_betaald.length > 0 && <Badge variant="outline" className="text-muted-foreground">{preview.reeds_betaald.length} reeds verwerkt</Badge>}
                {preview.niet_gevonden.length > 0 && <Badge variant="outline" className="text-amber-600 border-amber-300">{preview.niet_gevonden.length} niet gevonden</Badge>}
              </div>

              <div className="max-h-[44vh] overflow-y-auto space-y-3">
                {preview.matches.length > 0 && (
                  <div data-testid="bank-import-matches">
                    <div className="text-xs font-medium text-emerald-600 mb-1 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Wordt geboekt als betaald / deels betaald</div>
                    <div className="border border-border rounded-lg divide-y divide-border">
                      {preview.matches.map((m) => (
                        <div key={m.factuur_id} className="flex items-center justify-between gap-2 p-2.5" data-testid={`bank-match-${m.factuurnummer}`}>
                          <div className="min-w-0">
                            <span className="font-mono text-xs">{m.factuurnummer}</span>
                            <span className="text-muted-foreground text-xs ml-2">betaald {fmtDate(m.betaaldatum)}</span>
                            {m.nieuwe_status === "deels_betaald"
                              ? <Badge variant="outline" className="ml-2 text-amber-600 border-amber-300">Deels · {fmtEUR(m.openstaand_na)} open</Badge>
                              : <Badge variant="outline" className="ml-2 text-emerald-600 border-emerald-300">Volledig</Badge>}
                          </div>
                          <div className="text-right shrink-0">
                            <span className="font-medium">{fmtEUR(m.transactie_bedrag)}</span>
                            {Math.abs(m.verschil) > 0.01 && (
                              <span className="text-amber-600 text-xs ml-2" title={`Factuur: ${fmtEUR(m.factuur_bedrag)}`}>Δ {fmtEUR(m.verschil)}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {preview.credit_matches?.length > 0 && (
                  <div data-testid="bank-import-credit-matches">
                    <div className="text-xs font-medium text-rose-600 mb-1 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Creditfacturen — wordt op "verwerkt" gezet</div>
                    <div className="border border-border rounded-lg divide-y divide-border">
                      {preview.credit_matches.map((m) => (
                        <div key={m.factuur_id} className="flex items-center justify-between gap-2 p-2.5" data-testid={`bank-credit-match-${m.factuurnummer}`}>
                          <div className="min-w-0">
                            <span className="font-mono text-xs">{m.factuurnummer}</span>
                            <span className="text-muted-foreground text-xs ml-2">uitbetaald {fmtDate(m.verwerkt_op)}</span>
                            <Badge variant="outline" className="ml-2 text-rose-600 border-rose-300">Credit</Badge>
                          </div>
                          <div className="text-right shrink-0">
                            <span className="font-medium">{fmtEUR(m.transactie_bedrag)}</span>
                            {Math.abs(m.verschil) > 0.01 && (
                              <span className="text-amber-600 text-xs ml-2" title={`Factuur: ${fmtEUR(m.factuur_bedrag)}`}>Δ {fmtEUR(m.verschil)}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {preview.niet_gevonden.length > 0 && (
                  <div data-testid="bank-import-niet-gevonden">
                    <div className="text-xs font-medium text-amber-600 mb-1 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Geen factuurnummer herkend</div>
                    <div className="border border-border rounded-lg divide-y divide-border">
                      {preview.niet_gevonden.map((n, i) => (
                        <div key={i} className="flex items-center justify-between gap-2 p-2.5 text-muted-foreground">
                          <span className="truncate text-xs">{n.omschrijving}</span>
                          <span className="shrink-0 font-medium">{fmtEUR(n.bedrag)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {preview.reeds_betaald.length > 0 && (
                  <div>
                    <div className="text-xs font-medium text-muted-foreground mb-1 flex items-center gap-1"><Ban className="h-3 w-3" /> Overgeslagen (reeds betaald/gecrediteerd)</div>
                    <div className="flex flex-wrap gap-1.5">
                      {preview.reeds_betaald.map((r, i) => <code key={i} className="text-[11px] bg-secondary px-1.5 py-0.5 rounded">{r.factuurnummer}</code>)}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            {done ? (
              <Button onClick={() => setOpen(false)} data-testid="bank-import-close">Sluiten</Button>
            ) : (
              <>
                <Button variant="ghost" onClick={() => setOpen(false)}>Annuleren</Button>
                <Button onClick={apply} disabled={applying || loading || (!preview?.matches?.length && !preview?.credit_matches?.length)} data-testid="bank-import-apply">
                  {applying ? "Bezig…" : `Verwerk ${(preview?.matches?.length || 0) + (preview?.credit_matches?.length || 0)} regel(s)`}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
